// Query string contract for the projects overview page (WHN-2102/2104): the
// only place that knows param names; an absent param means "no restriction",
// except statuses, where it means the page's default (WHN-2212).
// Mirrors criteriaContract, but for the projects endpoint, which only filters
// on location/regions/statuses.

// Same default as the listing search (WHN-2172); the radius is always sent to
// the API (projectsApiParams), where an absent one would mean no limit at all.
export const DEFAULT_RADIUS = 25;

export const DEFAULT_SORT = 'name-asc';

// Filter-bar dot colors per status, the same values as the $project-status-*
// SCSS variables (_vars.scss); statuses without an agreed color are grey.
export const STATUS_COLORS = {
  'In ontwikkeling': '#858585',
  Verkoopvoorbereiding: '#f98100',
  Voorinschrijving: '#acc33e',
  Inschrijving: '#acc33e',
  'In verkoop': '#acc33e',
  'Start bouw': '#858585'
};

export const SORT_DISTANCE_ASC = 'distance-asc';

// status-asc/status-desc follow once the endpoint supports them (WHN-2118
// handoff); adding them now would 400 the search request.
export const SORT_OPTIONS = [
  { slug: SORT_DISTANCE_ASC, labelKey: 'projects-filter-bar.sort.distance-asc' },
  { slug: 'name-asc', labelKey: 'projects-filter-bar.sort.name-asc' },
  { slug: 'name-desc', labelKey: 'projects-filter-bar.sort.name-desc' },
  { slug: 'region-asc', labelKey: 'projects-filter-bar.sort.region-asc' },
  { slug: 'region-desc', labelKey: 'projects-filter-bar.sort.region-desc' }
];

// Distance measures from the search center, so it is only an option while a
// location is set: without long/lat the API falls back to the project name and
// the chosen label would not match the order it produces.
export const availableSortOptions = (criteria) =>
  SORT_OPTIONS.filter(
    (option) =>
      option.slug !== SORT_DISTANCE_ASC || (criteria.long !== null && criteria.lat !== null)
  );

// Folds a sort that is not on offer back onto the default, so clearing the
// location cannot leave distance selected.
export const resolveSort = (criteria) =>
  availableSortOptions(criteria).some((option) => option.slug === criteria.sort)
    ? criteria.sort
    : DEFAULT_SORT;

// Regions (id + name) and statuses come from Twig data attributes, not a
// hardcoded list: the backend owns both vocabularies. They are registered at
// mount, before useProjectCriteria first reads the querystring.
let validRegionIds = [];
let validStatuses = [];
// The statuses a page starts filtered on: "In ontwikkeling" on the projects
// overview, none on the region pages that share this app (WHN-2212)
let defaultStatuses = [];

export const configureProjectFilterSets = ({ regions = [], statuses = [], defaults = [] } = {}) => {
  validRegionIds = regions.map((region) => String(region.id));
  validStatuses = [...statuses];
  defaultStatuses = defaults.filter((status) => validStatuses.includes(status));
};

export const projectFilterSets = () => ({ regionIds: validRegionIds, statuses: validStatuses });

export const criteriaDefaults = () => ({
  loc: '',
  long: null,
  lat: null,
  radius: DEFAULT_RADIUS,
  regions: [],
  statuses: [...defaultStatuses],
  sort: DEFAULT_SORT
});

const CSV_PARAMS = ['regions', 'statuses'];

const isSameSet = (values, others) =>
  values.length === others.length && values.every((value) => others.includes(value));
const CRITERIA_PARAMS = ['loc', 'long', 'lat', 'radius', 'sort', ...CSV_PARAMS];

// The criteria the filter bar shows as pills. "Filters wissen" there owns only
// these; the search bar's location segment has its own clear button and must
// survive it (WHN-2127). The sheet's "Alle filters wissen" still resets
// everything through hasActiveCriteria/reset.
export const FILTER_BAR_KEYS = ['regions', 'statuses', 'sort'];

// "Filters wissen" returns to the page's initial state, so on the projects
// overview it restores the default status instead of clearing it (WHN-2212).
const isDefaultValue = (criteria, key, defaults) =>
  Array.isArray(defaults[key]) ? isSameSet(criteria[key], defaults[key]) : criteria[key] === defaults[key];

export const hasActiveFilterBarCriteria = (criteria) => {
  const defaults = criteriaDefaults();
  return FILTER_BAR_KEYS.some((key) => !isDefaultValue(criteria, key, defaults));
};

export const filterBarDefaults = () => {
  const defaults = criteriaDefaults();
  return Object.fromEntries(FILTER_BAR_KEYS.map((key) => [key, defaults[key]]));
};

export const toQueryParams = (criteria) => {
  const params = new URLSearchParams();

  // loc is only a filter together with its coordinates (see criteriaContract).
  if (criteria.loc && criteria.long !== null && criteria.lat !== null) {
    params.set('loc', criteria.loc);
    params.set('long', String(criteria.long));
    params.set('lat', String(criteria.lat));
    if (criteria.radius !== DEFAULT_RADIUS) {
      params.set('radius', criteria.radius === null ? '' : String(criteria.radius));
    }
  }

  if (criteria.regions.length) params.set('regions', criteria.regions.join(','));

  // Written whenever it departs from the default, as an empty value when no
  // status is chosen: without it a reload would land back on the default.
  if (!isSameSet(criteria.statuses, defaultStatuses)) params.set('statuses', criteria.statuses.join(','));

  if (criteria.sort !== DEFAULT_SORT) params.set('sort', criteria.sort);

  return params;
};

export const fromQueryParams = (searchParams) => {
  const criteria = criteriaDefaults();

  const long = Number.parseFloat(searchParams.get('long'));
  const lat = Number.parseFloat(searchParams.get('lat'));
  const hasCoordinates =
    Number.isFinite(long) && Number.isFinite(lat) && Math.abs(long) <= 180 && Math.abs(lat) <= 90;

  if (searchParams.has('loc') && hasCoordinates) {
    criteria.loc = searchParams.get('loc');
    criteria.long = long;
    criteria.lat = lat;
    if (searchParams.has('radius')) {
      const rawRadius = searchParams.get('radius');
      if (rawRadius === '') {
        criteria.radius = null;
      } else {
        const radius = Number.parseInt(rawRadius, 10);
        if (/^\d+$/.test(rawRadius) && radius >= 1 && radius <= 500) criteria.radius = radius;
      }
    }
  }

  CSV_PARAMS.forEach((key) => {
    if (searchParams.has(key)) criteria[key] = searchParams.get(key).split(',').filter(Boolean);
  });

  criteria.regions = criteria.regions.filter((id) => validRegionIds.includes(id));
  criteria.statuses = criteria.statuses.filter((status) => validStatuses.includes(status));

  const sort = searchParams.get('sort');
  if (sort && SORT_OPTIONS.some((option) => option.slug === sort)) criteria.sort = sort;
  criteria.sort = resolveSort(criteria);

  return criteria;
};

export const hasCriteriaParams = (searchParams) =>
  CRITERIA_PARAMS.some((param) => searchParams.has(param));
